/**
 * HLOS-21 — Serviço de biometria (camada 2 do vault).
 *
 * Wrapper testável em volta de `@aparajita/capacitor-biometric-auth`.
 *
 * Princípios constitucionais (CL-07, CL-08, Princípio X):
 *  - Biometria **NUNCA** substitui a passphrase/mnemonic do vault.
 *  - Biometria apenas **destrava um session key em memória por N minutos**
 *    (camada de conveniência acima do vault — vault permanece soberano).
 *  - Plataforma (Capacitor/iOS/Android) **NUNCA** vê a chave em plaintext
 *    (Secure Enclave / Keystore — biometria é gate local, não exporta nada).
 *  - **Fallback gracioso**: se biometria indisponível, chamar `passphraseFn`
 *    (CC-08 motivação cultivada: zero fricção viciante).
 *  - Cancelar biométrica **NUNCA** penaliza (CC-05).
 *
 * Smoke test em simulador:
 *   iOS:     pnpm cap:run:ios  → Face ID / Touch ID funciona em simulator
 *   Android: pnpm cap:run:android → Fingerprint simulado em emulator (config em AVD)
 *   Fallback: cancelar biometric prompt → passphraseFn é chamado
 *
 * Referências:
 *   - REQ-001 (Companion App)
 *   - HLOS-10 (Capacitor ship epic)
 *   - ADR-0002 (PWA + Capacitor shell)
 *   - CL-07 (Modelo = ativo / vault local-first)
 *   - CL-08 (Ética fechada / sem tracking, sem key escrow)
 */

import {
  BiometricAuth,
  BiometryType,
  BiometryError,
  BiometryErrorType,
  type CheckBiometryResult,
} from "@aparajita/capacitor-biometric-auth";
import type { BiometricAvailability, BiometricDecision } from "./types";

/** Opções para `authenticate()`. */
export interface AuthenticateOptions {
  /** Razão exibida ao usuário no prompt (idioma do app). */
  readonly reason: string;
  /** Texto do botão Cancelar (default: "Cancelar"). */
  readonly cancelTitle?: string;
  /** Permite credenciais do device (PIN/pattern) como fallback local. */
  readonly allowDeviceCredential?: boolean;
  /** iOS: título do botão de fallback (ex: "Use passphrase"). */
  readonly iosFallbackTitle?: string;
  /** Android: título do dialog (opcional, system default se vazio). */
  readonly androidTitle?: string;
  /** Android: subtítulo (opcional). */
  readonly androidSubtitle?: string;
}

/** Resultado de `authenticateOrFallback`. */
export type AuthenticateWithFallbackResult<T> =
  | { readonly outcome: "biometric_ok"; readonly value?: T }
  | { readonly outcome: "passphrase" }
  | { readonly outcome: "canceled" };

/**
 * Mapeia `BiometryType` (enum numérica) para um tipo semântico em string.
 * Útil para logs (privados — não user-facing) e para serialização cross-platform.
 */
function mapBiometryType(t: BiometryType): BiometricAvailability["type"] {
  switch (t) {
    case BiometryType.touchId:
      return "touchId";
    case BiometryType.faceId:
    case BiometryType.faceAuthentication:
      return "face";
    case BiometryType.fingerprintAuthentication:
      return "fingerprint";
    case BiometryType.irisAuthentication:
      return "iris";
    case BiometryType.none:
    default:
      return "none";
  }
}

/** Mapeia `BiometryError` para nosso `BiometricDecision`. */
function mapBiometryError(err: BiometryError): BiometricDecision {
  switch (err.code) {
    case BiometryErrorType.userCancel:
    case BiometryErrorType.systemCancel:
    case BiometryErrorType.appCancel:
      return { kind: "cancel" };
    case BiometryErrorType.userFallback:
      // Usuário pediu explicitamente fallback → treat como cancel (caller chama passphraseFn)
      return { kind: "cancel" };
    case BiometryErrorType.biometryNotAvailable:
    case BiometryErrorType.biometryNotEnrolled:
    case BiometryErrorType.passcodeNotSet:
    case BiometryErrorType.noDeviceCredential:
      return { kind: "fallback", reason: "unavailable" };
    default:
      return {
        kind: "error",
        code: String(err.code),
        message: err.message,
      };
  }
}

/**
 * Verifica se a biometria está disponível no device.
 *
 * Casos `available = false`:
 *  - Hardware ausente (emulador sem fingerprint configurado)
 *  - Usuário não cadastrou biometria
 *  - OS não suporta (versão antiga)
 *  - Web fallback (não há Capacitor) — retorna `{ available: false, reason: "os_unsupported" }`
 *
 * Política: **nunca lançar exceção** — retornar `{ available: false, ... }` em
 * qualquer falha (fail-safe → caller oferece fallback passphrase).
 */
export async function isBiometricAvailable(): Promise<BiometricAvailability> {
  try {
    const result: CheckBiometryResult = await BiometricAuth.checkBiometry();
    if (!result.isAvailable || result.biometryType === BiometryType.none) {
      return {
        available: false,
        type: "none",
        reason: result.isAvailable ? "not_enrolled" : "no_hardware",
      };
    }
    return { available: true, type: mapBiometryType(result.biometryType) };
  } catch (_err) {
    // Em web (vite dev sem Capacitor), o plugin lança — não é erro fatal.
    return {
      available: false,
      type: "none",
      reason: "os_unsupported",
    };
  }
}

/**
 * Tenta autenticar via biometria. **Não** chama fallback — caller decide.
 *
 * Retorna `BiometricDecision` (não boolean) para preservar o motivo:
 *  - "ok"      → sucesso; caller pode prosseguir (ex: destravar vault)
 *  - "cancel"  → usuário cancelou; caller decide (CL-05: zero penalidade)
 *  - "error"   → falha técnica; caller decide (ex: oferecer passphrase)
 *
 * Caller típico: `authenticateOrFallback()` (abaixo) ou UI explícita.
 */
export async function authenticate(
  options: AuthenticateOptions,
): Promise<BiometricDecision> {
  try {
    await BiometricAuth.authenticate({
      reason: options.reason,
      cancelTitle: options.cancelTitle ?? "Cancelar",
      allowDeviceCredential: options.allowDeviceCredential ?? true,
      iosFallbackTitle: options.iosFallbackTitle,
      androidTitle: options.androidTitle,
      androidSubtitle: options.androidSubtitle,
    });
    return { kind: "ok" };
  } catch (err) {
    if (err instanceof BiometryError) {
      return mapBiometryError(err);
    }
    return {
      kind: "error",
      code: "EXCEPTION",
      message: err instanceof Error ? err.message : String(err),
    };
  }
}

/**
 * Tenta autenticar via biometria; se indisponível, falhar, ou cancelar
 * explicitamente, chama `passphraseFn` (que tipicamente abre o prompt de
 * passphrase do vault — REQ-004).
 *
 * Política de fallback (HLOS-21 AC):
 *  - Biometria indisponível → `passphraseFn()` (não bloqueia learner)
 *  - Usuário cancela biometria → `passphraseFn()` (CC-05: zero penalidade)
 *  - Erro técnico → `passphraseFn()` (fail-safe)
 *  - Biometria OK → retorna `{ outcome: "biometric_ok" }` (caller prossegue)
 *
 * IMPORTANTE (CL-08): biometria NUNCA retorna o valor da chave.
 * O caller (vault unlock) chama `passphraseFn` (via WebCrypto + Argon2id)
 * — biometria só dá um "OK" semântico, NÃO exporta segredo.
 */
export async function authenticateOrFallback(
  options: AuthenticateOptions,
  passphraseFn: () => Promise<unknown>,
): Promise<AuthenticateWithFallbackResult<unknown>> {
  const availability = await isBiometricAvailable();
  if (!availability.available) {
    await passphraseFn();
    return { outcome: "passphrase" };
  }

  const decision = await authenticate(options);
  if (decision.kind === "ok") {
    return { outcome: "biometric_ok" };
  }
  // "cancel" ou "error" ou "fallback" → passphraseFn. Nunca bloqueia, nunca penaliza (CC-05).
  await passphraseFn();
  return { outcome: "passphrase" };
}
